import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Mail, User, Shield, Phone, Info, KeyRound, MapPin, ExternalLink, Globe, Award, Building2, Sparkles } from 'lucide-react';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube, FaCcVisa, FaCcMastercard, FaCcAmex, FaGooglePay, FaUniversity, FaCreditCard } from 'react-icons/fa';

const Footer = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (name && email) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 3000);
      setName('');
      setEmail('');
    }
  };

  const paymentMethods = [
    { name: 'Visa', icon: <FaCcVisa className="w-8 h-8" /> },
    { name: 'MasterCard', icon: <FaCcMastercard className="w-8 h-8" /> },
    { name: 'Amex', icon: <FaCcAmex className="w-8 h-8" /> },
    { name: 'RuPay', icon: <FaCreditCard className="w-7 h-7" /> },
    { name: 'UPI', icon: <FaGooglePay className="w-7 h-7" /> },
    { name: 'Net Banking', icon: <FaUniversity className="w-6 h-6" /> },
  ];

  const memberOrgs = [
    { name: 'IATA', icon: <Globe className="w-5 h-5" />, desc: 'International Air Transport Association' },
    { name: 'TAAI', icon: <Award className="w-5 h-5" />, desc: 'Travel Agents Association of India' },
    { name: 'TAFI', icon: <Building2 className="w-5 h-5" />, desc: 'Travel Agents Federation of India' },
  ];

  return (
    <footer className="bg-[#1B375B] text-white pb-20 md:pb-0">{/* Add padding for bottom nav on mobile */}
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-[#335E77] to-[#659A9B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-1">Subscribe to</h3>
              <p className="text-white/80 text-base">Receive Latest Travel Updates</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  placeholder="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 pr-4 py-3 bg-white/15 border border-white/25 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:border-white transition-colors w-full sm:w-48"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="email"
                  placeholder="Email ID"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 pr-4 py-3 bg-white/15 border border-white/25 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:border-white transition-colors w-full sm:w-56"
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-white text-[#335E77] font-semibold rounded-lg hover:bg-[#E7EEEE] transition-colors duration-300 shadow-lg"
              >
                {subscribed ? 'Subscribed!' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img 
                src="/acf-tourism-logo.png" 
                alt="ACF Tourism BETA" 
                className="h-10 w-auto max-w-[210px] object-contain object-left"
                width="210"
                height="46"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              Fly From Anywhere, To Anywhere. Your trusted travel partner for booking the best flight deals worldwide.
            </p>
            <div className="flex gap-3 mt-5">
              {[FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube].map((Icon, i) => (
                <button
                  key={i}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#659A9B] transition-colors duration-300"
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold mb-5 text-[#659A9B] uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about-us" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-about-link">
                  <Info className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/why-acf-tourism" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-why-link">
                  <Sparkles className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Why ACF Tourism
                </Link>
              </li>
              <li>
                <Link to="/contact-us" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-contact-link">
                  <Phone className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/agent/login" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group">
                  <KeyRound className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Agent Login
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-privacy-link">
                  <Shield className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-terms-link">
                  <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-refund-link">
                  <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/cancellation-policy" className="flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors group" data-testid="footer-cancellation-link">
                  <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-[#659A9B] transition-colors" />
                  Cancellation Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* We Accept + Member Of */}
          <div>
            <h4 className="text-sm font-semibold mb-4 text-[#659A9B] uppercase tracking-wider">We Accept</h4>
            <div className="grid grid-cols-3 gap-2.5">
              {paymentMethods.map((method) => (
                <div
                  key={method.name}
                  className="flex flex-col items-center gap-1 px-2 py-2.5 bg-white/[0.06] border border-white/10 rounded-lg hover:bg-white/[0.12] hover:border-[#659A9B]/40 transition-all cursor-default"
                >
                  <div className="text-white/80">{method.icon}</div>
                  <span className="text-[9px] text-white/50 font-medium">{method.name}</span>
                </div>
              ))}
            </div>

            <h4 className="text-sm font-semibold mt-6 mb-3 text-[#659A9B] uppercase tracking-wider">Member of</h4>
            <div className="flex gap-2.5">
              {memberOrgs.map((org) => (
                <div
                  key={org.name}
                  className="flex flex-col items-center gap-1.5 px-3 py-3 bg-white/[0.06] border border-white/10 rounded-lg hover:bg-white/[0.12] hover:border-[#659A9B]/40 transition-all cursor-default flex-1"
                >
                  <div className="w-10 h-10 rounded-full bg-[#659A9B]/20 flex items-center justify-center text-[#659A9B]">
                    {org.icon}
                  </div>
                  <span className="text-xs font-bold text-white/90">{org.name}</span>
                  <span className="text-[8px] text-white/40 text-center leading-tight">{org.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold mb-5 text-[#659A9B] uppercase tracking-wider">Get in Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#659A9B] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/70 leading-relaxed">Mumbai, Maharashtra, India</p>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#659A9B] flex-shrink-0" />
                <p className="text-sm text-white/70">+91 98765 43210</p>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#659A9B] flex-shrink-0" />
                <p className="text-sm text-white/70">info@acftourism.com</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 pt-6 border-t border-white/10 text-center">
          <p className="text-white/50 text-sm">
            Copyright &copy; 2023 www.acftourism.com All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
